import { isInterestCreditDayAllowed } from '@/lib/userPlatformSchedule';
import { localDateKey, addLocalDateKey, sanitizeTimezone } from '@/lib/userTimezone';

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function utcDayKey(date = new Date()) {
  return new Date(date).toISOString().slice(0, 10);
}

function parseDayKey(key) {
  const [y, m, d] = String(key || '').split('-').map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
}

function addUtcDays(key, days) {
  const ms = parseDayKey(key);
  if (Number.isNaN(ms)) return key;
  return utcDayKey(new Date(ms + days * 86400000));
}

/** Inclusive calendar-day count between two YYYY-MM-DD keys (UTC). */
export function countUtcDaysInclusive(fromKey, toKey) {
  if (!fromKey || !toKey || fromKey > toKey) return 0;
  const diff = Math.floor((parseDayKey(toKey) - parseDayKey(fromKey)) / 86400000);
  return diff + 1;
}

/** Mon–Fri days between local date keys (inclusive), using the user's timezone. */
export function countWeekdayLocalDaysInclusive(fromKey, toKey, timezone = 'UTC') {
  const tz = sanitizeTimezone(timezone);
  if (!fromKey || !toKey || fromKey > toKey) return 0;

  let count = 0;
  let key = fromKey;
  while (key <= toKey) {
    if (isWeekdayLocalDateKey(key, tz)) count += 1;
    key = addLocalDateKey(key, 1);
  }
  return count;
}

function isWeekdayLocalDateKey(key, timezone) {
  const [y, m, d] = String(key || '').split('-').map(Number);
  if (!y || !m || !d) return false;
  const at = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return isInterestCreditDayAllowed(timezone, at).ok;
}

function resolveInterestTimezone(inv, timezone) {
  return sanitizeTimezone(timezone || inv?.termTimezone || 'UTC');
}

/**
 * Latest local calendar day fully ended before `through` (interest starts after midnight).
 * Purchase time on day D does not count until D has completed (credited from D+1 00:00).
 */
export function lastCompleteLocalDayKey(through = new Date(), timezone = 'UTC') {
  const throughKey = localDateKey(through, timezone);
  return addLocalDateKey(throughKey, -1);
}

/**
 * Pending interest days between last credit and `through`.
 * Returns weekday keys to credit and the last calendar day processed (inclusive).
 */
export function pendingInterestCreditSchedule(inv, through = new Date(), timezone = 'UTC') {
  const tz = resolveInterestTimezone(inv, timezone);
  const startKey = localDateKey(inv?.startedAt || new Date(), tz);
  const endKey = localDateKey(inv?.endsAt || through, tz);
  const completeThrough = lastCompleteLocalDayKey(through, tz);
  const lastEligible = completeThrough < endKey ? completeThrough : endKey;

  const lastCredited = String(inv?.lastInterestCreditedOn || '').trim();
  const fromKey = lastCredited ? addLocalDateKey(lastCredited, 1) : startKey;

  if (fromKey > lastEligible) {
    return { fromKey: '', toKey: '', days: 0, weekdayKeys: [] };
  }

  const weekdayKeys = [];
  let key = fromKey;
  while (key <= lastEligible) {
    if (isWeekdayLocalDateKey(key, tz)) weekdayKeys.push(key);
    key = addLocalDateKey(key, 1);
  }

  return {
    fromKey,
    toKey: lastEligible,
    days: weekdayKeys.length,
    weekdayKeys,
  };
}

export function resolveDailyRateFromInvestment(inv) {
  const stored = Number(inv?.dailyRate || 0);
  if (stored > 0) return stored;
  const monthly = Number(inv?.monthlyRate || 0);
  if (monthly > 0) return monthly / 30;
  return 0;
}

export function planDailyInterestAmount(principal, dailyRate) {
  return round2(Number(principal || 0) * Number(dailyRate || 0));
}

/** Total interest earned through `at` (credited + pending), weekdays only. */
export function totalInterestThrough(inv, at = new Date(), timezone) {
  const tz = resolveInterestTimezone(inv, timezone);
  const principal = Number(inv?.amount || 0);
  const dailyRate = resolveDailyRateFromInvestment(inv);
  const expectedCap = round2(Number(inv?.expectedInterest || 0));
  if (!principal || !dailyRate) return 0;

  const startKey = localDateKey(inv?.startedAt || new Date(), tz);
  const endKey = localDateKey(inv?.endsAt || at, tz);
  const completeThrough = lastCompleteLocalDayKey(at, tz);
  const lastKey = completeThrough < endKey ? completeThrough : endKey;
  if (lastKey < startKey) return 0;

  const days = countWeekdayLocalDaysInclusive(startKey, lastKey, tz);
  const perDay = planDailyInterestAmount(principal, dailyRate);
  const raw = round2(perDay * days);
  if (expectedCap > 0) return round2(Math.min(raw, expectedCap));
  return raw;
}

export function pendingCreditDays(inv, through = new Date(), timezone = 'UTC') {
  const { fromKey, toKey, days } = pendingInterestCreditSchedule(inv, through, timezone);
  return { fromKey, toKey, days };
}

export function pendingInterestCreditAmount(inv, through = new Date(), timezone = 'UTC') {
  const { days } = pendingInterestCreditSchedule(inv, through, timezone);
  if (days <= 0) return 0;

  const principal = Number(inv?.amount || 0);
  const dailyRate = resolveDailyRateFromInvestment(inv);
  const perDay = planDailyInterestAmount(principal, dailyRate);
  const raw = round2(perDay * days);

  const expectedCap = round2(Number(inv?.expectedInterest || 0));
  const already = round2(Number(inv?.interestCreditedTotal || 0));
  if (expectedCap > 0) return round2(Math.max(0, Math.min(raw, expectedCap - already)));
  return raw;
}

export function formatDailyRatePct(inv) {
  const pct = resolveDailyRateFromInvestment(inv) * 100;
  return pct.toFixed(4).replace(/\.?0+$/, '');
}
